import type { ComponentType, SVGProps } from 'react';
import {
  Sparkles,
  CircleAlert,
  CheckCircle,
  CheckSquare,
  Clock,
  Users,
} from 'lucide-react';
import { TaskEventDateIcon } from '@/components/ui';
import { formatReceivedTimestamp } from '@/lib/date';
import type { Notification, NotificationType } from '@/types';

/**
 * Structural type that matches both lucide icons and our custom icon
 * components (e.g. TaskEventDateIcon). Callers render with
 * `<Icon size={16} />` so we only need those two props for type safety.
 */
type NotificationIconComponent = ComponentType<
  SVGProps<SVGSVGElement> & { size?: number | string }
>;

// ── Time grouping ─────────────────────────────────────────────────

export type TimeGroup = 'Today' | 'Yesterday' | 'Earlier this week' | 'Older';

function getTimeGroup(createdAt: string): TimeGroup {
  const now = new Date();
  const date = new Date(createdAt);

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - todayStart.getDay());

  if (date >= todayStart) return 'Today';
  if (date >= yesterdayStart) return 'Yesterday';
  if (date >= weekStart) return 'Earlier this week';
  return 'Older';
}

/** Group a flat notification list into time buckets (preserving order). */
export function groupByTime(
  items: Notification[],
): { group: TimeGroup; items: Notification[] }[] {
  const groups = new Map<TimeGroup, Notification[]>();
  for (const item of items) {
    const group = getTimeGroup(item.created_at);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(item);
  }
  return (['Today', 'Yesterday', 'Earlier this week', 'Older'] as TimeGroup[])
    .filter((g) => groups.has(g))
    .map((g) => ({ group: g, items: groups.get(g)! }));
}

// ── Timestamp formatting ──────────────────────────────────────────

/**
 * The Activity feed's timestamp, on the same four-rung rule as the Review
 * card's "received" timestamp (YAR-310 CB6) — see
 * {@link formatReceivedTimestamp} for the rungs and why. Kept as its own
 * export (delegating rather than inlined at the one call site) so
 * `NotificationItem` doesn't have to change its import if this feed ever
 * needs a rule of its own again.
 */
export function formatTimestamp(createdAt: string): string {
  return formatReceivedTimestamp(createdAt);
}

// ── Icon mapping ──────────────────────────────────────────────────

export type IconVariant = 'default' | 'error' | 'success' | 'avatar';

const ICON_MAP: Record<string, NotificationIconComponent> = {
  // AI/System
  review_ready: Sparkles,
  batch_processing_complete: Sparkles,
  email_processing_failed: CircleAlert,
  forwarding_setup_confirmed: CheckCircle,
  // Task
  task_completed: CheckSquare,
  task_assigned: CheckSquare,
  task_created: CheckSquare,
  task_due_date_changed: CheckSquare,
  task_location_changed: CheckSquare,
  task_restored: CheckSquare,
  // Event
  event_date_time_changed: TaskEventDateIcon,
  event_location_changed: TaskEventDateIcon,
  event_collaborator_added: TaskEventDateIcon,
  event_created: TaskEventDateIcon,
  // Household (member_joined/member_left use avatar, not icon)
  access_level_changed: Users,
  member_access_changed: Users,
  // Reminders
  upcoming_event: Clock,
  task_due_soon: Clock,
  // Fallbacks
  commit_success: TaskEventDateIcon,
  system: CircleAlert,
};

/** Returns the lucide-react icon for a notification type. */
export function getNotificationIcon(
  type: NotificationType,
): NotificationIconComponent {
  return ICON_MAP[type] ?? CircleAlert;
}

const VARIANT_MAP: Partial<Record<NotificationType, IconVariant>> = {
  email_processing_failed: 'error',
  forwarding_setup_confirmed: 'success',
  member_joined: 'avatar',
  member_left: 'avatar',
};

/** Returns the icon variant (for background color styling). */
export function getNotificationIconVariant(
  type: NotificationType,
): IconVariant {
  return VARIANT_MAP[type] ?? 'default';
}

/** Whether this notification type uses the actor's avatar instead of an icon. */
export function isAvatarType(type: NotificationType): boolean {
  return type === 'member_joined' || type === 'member_left';
}
